import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STEPS = ["About You", "Experience", "Hull Preference", "Boarding Fee"];

export default function FoundryInterview() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { search } = useLocation();
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        full_name: user && typeof user === "object" ? user.name || "" : "",
        location: "",
        linkedin: "",
        niche: "",
        years_experience: "",
        prior_placements: "",
        tools: "",
        why_sovereign: "",
        requested_subdomain: "",
        intro_video_url: "",
    });

    if (!user || typeof user !== "object") {
        return (
            <div className="mx-auto max-w-md px-6 py-24 text-center">
                <div className="caption text-[var(--brand-accent)]">Director Interview</div>
                <h1 className="mt-3 font-serif text-4xl tracking-display text-[#FAFAFA]">Sign in to begin</h1>
                <p className="mt-3 text-[#A3A3A3]">Recruiter authentication is required for the Wayfinder.</p>
                <button
                    onClick={() => navigate({ pathname: "/login", search: `?next=/foundry/interview` })}
                    className="mt-8 rounded-sm px-6 py-3 text-sm font-medium"
                    style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                >
                    Sign in
                </button>
            </div>
        );
    }

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    function canAdvance() {
        if (step === 0) return form.full_name && form.location && form.niche;
        if (step === 1) return form.years_experience !== "" && form.prior_placements.length >= 10;
        if (step === 2) return form.why_sovereign.length >= 10 && form.requested_subdomain.length >= 2;
        return true;
    }

    async function submitInterview() {
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                years_experience: parseInt(form.years_experience || "0", 10),
                requested_subdomain: form.requested_subdomain.trim().toLowerCase(),
                linkedin: form.linkedin.trim() || null,
                tools: form.tools.trim() || null,
                intro_video_url: form.intro_video_url.trim() || null,
            };
            await api.post("/foundry/interview", payload);
            toast.success("Interview saved. Time to commission your hull.");
            setStep(3);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div data-testid="foundry-interview-page" className="mx-auto max-w-3xl px-6 py-16 md:px-10">
            <div className="caption text-[var(--brand-accent)]">Director Interview · Wayfinder</div>
            <h1 className="mt-3 font-serif text-5xl tracking-display text-[#FAFAFA]">Commission your Hull</h1>

            {/* Stepper */}
            <ol className="mt-10 grid grid-cols-4 gap-2">
                {STEPS.map((label, i) => (
                    <li
                        key={label}
                        data-testid={`step-${i}`}
                        className="flex flex-col gap-2"
                    >
                        <div
                            className="h-0.5 w-full"
                            style={{
                                background: i <= step ? "var(--brand-accent)" : "rgba(255,255,255,0.08)",
                            }}
                        />
                        <div className="flex items-baseline gap-2">
                            <span className="font-mono text-xs text-[#737373]">0{i + 1}</span>
                            <span className={`text-xs ${i === step ? "text-[#FAFAFA]" : "text-[#737373]"}`}>{label}</span>
                        </div>
                    </li>
                ))}
            </ol>

            <div className="mt-12 rounded-md border border-white/5 bg-[#141414]/60 p-8 backdrop-blur-xl sovereign-shadow">
                {step === 0 && (
                    <div className="grid gap-5 md:grid-cols-2">
                        <Field label="Full name" testId="iv-full_name" value={form.full_name} onChange={update("full_name")} />
                        <Field label="Location (city, country)" testId="iv-location" value={form.location} onChange={update("location")} />
                        <Field label="LinkedIn URL (optional)" testId="iv-linkedin" value={form.linkedin} onChange={update("linkedin")} wide />
                        <Field label="Recruiting niche / sector" testId="iv-niche" value={form.niche} onChange={update("niche")} wide placeholder="e.g. Executive Tech, Luxury Hospitality, Quant Finance" />
                    </div>
                )}

                {step === 1 && (
                    <div className="grid gap-5 md:grid-cols-2">
                        <Field label="Years of recruiting experience" type="number" testId="iv-years" value={form.years_experience} onChange={update("years_experience")} />
                        <Field label="Tools you use (optional)" testId="iv-tools" value={form.tools} onChange={update("tools")} placeholder="LinkedIn Recruiter, Gem, Greenhouse…" />
                        <TextArea label="Sample placements (anonymise as needed)" testId="iv-placements" value={form.prior_placements} onChange={update("prior_placements")} rows={6} />
                    </div>
                )}

                {step === 2 && (
                    <div className="grid gap-5">
                        <TextArea label="Why Sovereign? What kind of hull do you want?" testId="iv-why" value={form.why_sovereign} onChange={update("why_sovereign")} rows={6} />
                        <div>
                            <label className="caption text-[#737373]">Your hull subdomain</label>
                            <div className="mt-2 flex items-stretch overflow-hidden rounded-sm border border-white/10 bg-white/[0.03] focus-within:border-[var(--brand-accent)]">
                                <input
                                    data-testid="iv-subdomain"
                                    value={form.requested_subdomain}
                                    onChange={(e) => setForm({ ...form, requested_subdomain: e.target.value.toLowerCase() })}
                                    placeholder="tyronda"
                                    className="flex-1 bg-transparent px-4 py-3 text-sm text-[#FAFAFA] focus:outline-none"
                                />
                                <span className="flex items-center bg-black/40 px-4 text-sm text-[#737373]">
                                    .bizboogie.com
                                </span>
                            </div>
                            <p className="mt-2 text-xs text-[#737373]">
                                Letters, numbers and hyphens only. Your Ghost Crew will provision this for you after payment.
                            </p>
                        </div>
                        <div>
                            <label className="caption text-[#737373]">Intro video <span className="text-[#525252]">(optional · YouTube, Loom, or Vimeo URL · 30 sec recommended)</span></label>
                            <input
                                data-testid="iv-intro_video"
                                value={form.intro_video_url}
                                onChange={update("intro_video_url")}
                                placeholder="https://youtu.be/… or https://loom.com/share/…"
                                className="mt-2 w-full rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] placeholder:text-white/30 focus:border-[var(--brand-accent)] focus:outline-none"
                            />
                            <p className="mt-2 text-xs text-[#737373]">
                                Featured on the Sovereign Star feed once you board. Pitch yourself in 30 seconds.
                            </p>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="text-center py-6" data-testid="iv-step-payment">
                        <div className="caption text-[var(--brand-accent)]">Final Step · The Commission</div>
                        <h2 className="mt-3 font-serif text-3xl tracking-display text-[#FAFAFA]">
                            Board the Fleet instantly with PayPal.
                        </h2>
                        <p className="mt-3 italic text-[#A3A3A3]">
                            "No financial blockade can stop a Sovereign signal."
                        </p>
                        <button
                            data-testid="cta-go-to-checkout"
                            onClick={() => navigate({ pathname: "/checkout", search })}
                            className="mt-8 inline-flex items-center justify-center gap-2 rounded-sm px-8 py-4 text-sm font-medium transition-all duration-300 hover:shadow-[0_0_24px_rgba(201,169,110,0.35)]"
                            style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                        >
                            Pay $250 with PayPal — Fastest Selection
                        </button>
                        <p className="mt-6 caption text-[#737373]">
                            On payment success, your hull `{form.requested_subdomain || "<subdomain>"}.bizboogie.com` enters the Ghost Crew provisioning queue.
                        </p>
                    </div>
                )}

                {step < 3 && (
                    <div className="mt-8 flex items-center justify-between">
                        <button
                            data-testid="iv-back"
                            disabled={step === 0}
                            onClick={() => setStep(Math.max(0, step - 1))}
                            className="rounded-sm border border-white/10 px-6 py-2.5 text-sm text-[#FAFAFA] hover:bg-white/5 disabled:opacity-30"
                        >
                            ← Back
                        </button>
                        {step < 2 ? (
                            <button
                                data-testid="iv-next"
                                disabled={!canAdvance()}
                                onClick={() => setStep(step + 1)}
                                className="rounded-sm px-6 py-2.5 text-sm font-medium transition-all duration-300 disabled:opacity-40"
                                style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                            >
                                Next →
                            </button>
                        ) : (
                            <button
                                data-testid="iv-submit"
                                disabled={!canAdvance() || submitting}
                                onClick={submitInterview}
                                className="rounded-sm px-6 py-2.5 text-sm font-medium transition-all duration-300 disabled:opacity-40"
                                style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                            >
                                {submitting ? "Saving…" : "Submit Interview →"}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, testId, wide, ...rest }) {
    return (
        <div className={wide ? "md:col-span-2" : undefined}>
            <label className="caption text-[#737373]">{label}</label>
            <input
                data-testid={testId}
                {...rest}
                className="mt-2 w-full rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] placeholder:text-white/30 focus:border-[var(--brand-accent)] focus:outline-none"
            />
        </div>
    );
}

function TextArea({ label, testId, rows = 4, ...rest }) {
    return (
        <div className="md:col-span-2">
            <label className="caption text-[#737373]">{label}</label>
            <textarea
                data-testid={testId}
                rows={rows}
                {...rest}
                className="mt-2 w-full rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[#FAFAFA] placeholder:text-white/30 focus:border-[var(--brand-accent)] focus:outline-none"
            />
        </div>
    );
}
